import { makeStyles } from '@mui/styles';
import MenuItem from 'apollo-react/components/MenuItem';
import Radio from 'apollo-react/components/Radio';
import RadioGroup from 'apollo-react/components/RadioGroup';
import Select from 'apollo-react/components/Select';
import TextField from 'apollo-react/components/TextField';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';

import { OrderByTable } from './OrderByTableComponent';
import {
  getFormDataObjectList,
  isResolver,
  validateDatasource,
  validateDatasourceName,
} from './utils';
import {
  getDatasourceFields,
  getEntityCatalog,
  getExternalDatasources,
  getListOptions,
} from '../../../../../api/forms';
import { useFormSelector } from '../../../../../custom-hooks';
import { useTranslation } from '../../../../../custom-hooks/useTranslation';
import { getUITA, isTrue } from '../../../../../utils/commonUtils';
import { getEnvVar } from '../../../../../utils/env';
import {
  DATASOURCE_LISTDATA_DEFAULT_FIELDS,
  DATASOURCE_TYPE_ENUM,
  DATASOURCE_TYPE_EXTERNALDATASOURCE,
  DATASOURCE_TYPE_FORMDATA,
  DATASOURCE_TYPE_LISTDATA,
  DATASOURCE_TYPE_SETTINGS,
  DATASOURCE_TYPE_SMARTSOLVEDATA,
  ERROR_LABEL_DATA_KEY_FIELD,
  ERROR_LABEL_DATASOURCE_KEY,
} from '../../../../constants/editor.constants';
import {
  getEntityDefinition,
  getToken,
  getTranslations,
} from '../../../../selectors/common.selectors';
import { changeDataSource } from '../../../../utils/editor.utils';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  rowLabel: {
    paddingTop: 8,
    paddingRight: 8,
    alignItems: 'center',
    width: '25%',
  },
  rowColumn: {
    flex: 1,
    alignItems: 'center',
    maxWidth: '50%',
  },
  radio: {
    display: 'flex',
    flexDirection: 'row',
  },
});

const TextControl = ({ currentValue, errorLabel, disabled, onBlur, errors, label, uita = '' }) => {
  const [value, setValue] = useState(currentValue);
  const classes = useStyles();
  const translateString = useTranslation();
  const errorMessage = translateString(errors[errorLabel]);

  useEffect(() => {
    setValue(currentValue);
  }, [currentValue]);

  return (
    <>
      <div className={classes.rowLabel}>
        <span>{label}</span>
      </div>
      <div className={classes.rowColumn}>
        <TextField
          ui-ta={uita}
          fullWidth
          disabled={disabled}
          value={value}
          onChange={({ target }) => setValue(target.value)}
          onBlur={onBlur}
          error={!!errorMessage}
          helperText={errorMessage}
        />
      </div>
    </>
  );
};

const DatasourceModal = ({
  dataSources,
  dataSourceObject,
  setAllowFilterEdit,
  setDatasourceObject,
  setIsDatasourceValid,
  dataSourceEditAction,
  setTempFilterConditions,
  setDataSourceFilterColumns,
}) => {
  const classes = useStyles();
  const [dataSourceKey, setDataSourceKey] = useState('');
  const [dataLookupType, setDataLookupType] = useState();
  const [dataObjectList, setDataObjectList] = useState([]);
  const [dataObjectValue, setDataObjectValue] = useState();
  const [fieldList, setFieldList] = useState([]);
  const [orderByList, setOrderByList] = useState([]);
  const [selectedFields, setSelectedFields] = useState([]);
  const [cacheData, setCacheData] = useState(false);
  const [dataKeyField, setDataKeyField] = useState('');
  const [errors, setErrors] = useState({});

  const entityDefinition = useFormSelector(getEntityDefinition);
  const translations = useFormSelector(getTranslations);
  const { Entity: entityTranslations } = translations;
  const token = useFormSelector(getToken);

  const formDataObjectList = useMemo(
    () => getFormDataObjectList({ entityDefinition, entityTranslations }),
    [entityDefinition, entityTranslations]
  );

  const setEntityCatalogList = (entityCatalog) => {
    setDataObjectList(
      entityCatalog?.Entity.map((entity) => ({
        label: entity?.Label,
        value: entity?.Label,
      })).sort((a, b) => a?.Label?.localeCompare(b?.Label))
    );
  };

  useEffect(() => {
    if (dataSourceObject && dataSourceObject?.dataSourceProperties?.dataSourceName) {
      const { dataSourceKey } = dataSourceObject;
      const {
        dataSourceName,
        dataSourceType,
        columnList,
        cacheData,
        dataKeyField,
        dataObjectList,
        orderBy,
      } = dataSourceObject?.dataSourceProperties || {};
      if (dataSourceType === DATASOURCE_TYPE_SMARTSOLVEDATA) {
        getEntityCatalog({ entityKey: dataObjectValue }).then((res) => {
          const { entityCatalog, hasError } = res;
          !hasError && entityCatalog && setEntityCatalogList(entityCatalog);
        });
      } else if (dataSourceType === DATASOURCE_TYPE_FORMDATA) {
        setDataObjectList(formDataObjectList);
      } else if (dataLookupType === DATASOURCE_TYPE_SETTINGS) {
        setDataObjectList([{ value: 'Preferred Languages', label: 'Preferred Languages' }]);
      } else {
        setDataObjectList(dataObjectList);
      }
      setDataSourceKey(dataSourceKey);
      setDataLookupType(dataSourceType);
      setDataObjectValue(dataSourceName);
      setSelectedFields(columnList);
      setCacheData(isTrue(cacheData));
      setDataKeyField(dataKeyField);
      setOrderByList(orderBy);
    } else {
      const { dataSourceType } = dataSourceObject?.dataSourceProperties || {};
      setDataLookupType(dataSourceType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSourceObject]);

  useEffect(() => {
    setDataSourceFilterColumns(fieldList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldList]);

  const { dataSourceKey: dataSourceObjectKey, dataSourceProperties } = dataSourceObject || {};

  useEffect(() => {
    const isDatasourceValid = validateDatasource({
      dataSourceObject,
      dataSources,
      dataSourceEditAction,
      selectedFields,
    });

    setIsDatasourceValid(isDatasourceValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSourceObjectKey, selectedFields, dataSourceProperties?.orderBy]);

  useEffect(() => {
    try {
      if (dataObjectValue?.trim().length > 0 && dataLookupType > 0) {
        setAllowFilterEdit(true);
      } else {
        setAllowFilterEdit(false);
      }

      if (dataLookupType && dataLookupType > 0) {
        if (dataLookupType === DATASOURCE_TYPE_SMARTSOLVEDATA) {
          getEntityCatalog({ entityKey: dataObjectValue }).then((res) => {
            const { entityCatalog, hasError } = res;
            if (!hasError) {
              if (entityCatalog) {
                setEntityCatalogList(entityCatalog);
              } else {
                if (res[dataObjectValue]) {
                  setDataKeyField(res[dataObjectValue].PrimaryKeyColumn);
                  changeDataSource({
                    dataSourceObject,
                    property: 'dataKeyField',
                    propertyValue: res[dataObjectValue].PrimaryKeyColumn,
                    setDatasourceObject,
                  });

                  const textField = res[dataObjectValue].TextField;
                  const valueField = res[dataObjectValue].ValueField;

                  setDatasourceObject(dataSourceObject);
                  getDatasourceFields({
                    entityKey: dataObjectValue,
                    dataLookupType: DATASOURCE_TYPE_ENUM[dataLookupType],
                  }).then((res) => {
                    const { hasError, fields } = res;
                    if (!hasError && fields) {
                      const fieldList = fields
                        .filter((field) => field.IsHidden === false)
                        .map((field) => ({ ...field, value: field?.Name, label: field?.Label }))
                        .sort((a, b) => a?.Label?.localeCompare(b?.Label));
                      setFieldList(fieldList);

                      let columnList = [];
                      if (
                        dataObjectValue &&
                        dataSourceObject.dataSourceProperties.columnList.length
                      ) {
                        columnList = dataSourceObject.dataSourceProperties.columnList;
                      } else if (dataObjectValue) {
                        if (
                          textField &&
                          textField !== valueField &&
                          _.find(fieldList, { Name: textField, IsHidden: false })
                        ) {
                          columnList.push(textField);
                        }

                        if (
                          valueField &&
                          _.find(fieldList, { Name: valueField, IsHidden: false })
                        ) {
                          columnList.push(valueField);
                        }
                      }

                      setSelectedFields(columnList);
                      changeDataSource({
                        dataSourceObject,
                        property: 'columnList',
                        propertyValue: columnList,
                        setDatasourceObject,
                      });
                    }
                  });
                }
              }
            }
          });
        } else if (dataLookupType === DATASOURCE_TYPE_LISTDATA) {
          getListOptions({ entityKey: dataObjectValue }).then((res) => {
            const { ListOptions, hasError } = res;
            if (!hasError) {
              if (ListOptions) {
                setDataObjectList(
                  ListOptions?.map((option) => ({
                    label: option.LST_OBJECT_NAME,
                    value: option.LST_OBJECT_NAME,
                  })).sort((a, b) => a?.LST_OBJECT_NAME?.localeCompare(b?.LST_OBJECT_NAME))
                );

                getDatasourceFields({
                  entityKey: dataObjectValue,
                  dataLookupType: DATASOURCE_TYPE_ENUM[dataLookupType],
                }).then((res) => {
                  const { hasError, fields } = res;
                  if (!hasError && fields) {
                    const fieldList = fields
                      .filter((field) => field?.IsHidden === false)
                      .map((field) => ({ ...field, value: field?.Name, label: field?.Label }))
                      .sort((a, b) => a?.Label?.localeCompare(b?.Label));
                    setFieldList(fieldList);
                  }
                });

                if (dataObjectValue && dataSourceObject.dataSourceProperties.columnList.length) {
                  setSelectedFields(dataSourceObject.dataSourceProperties.columnList);
                  changeDataSource({
                    dataSourceObject,
                    property: 'columnList',
                    propertyValue: dataSourceObject.dataSourceProperties.columnList,
                    setDatasourceObject,
                  });
                } else if (dataObjectValue) {
                  setSelectedFields(DATASOURCE_LISTDATA_DEFAULT_FIELDS);
                  changeDataSource({
                    dataSourceObject,
                    property: 'columnList',
                    propertyValue: DATASOURCE_LISTDATA_DEFAULT_FIELDS,
                    setDatasourceObject,
                  });
                } else {
                  setSelectedFields([]);
                  changeDataSource({
                    dataSourceObject,
                    property: 'columnList',
                    propertyValue: [],
                    setDatasourceObject,
                  });
                }
                setDatasourceObject(dataSourceObject);
              }
            }
          });
        } else if (dataLookupType === DATASOURCE_TYPE_FORMDATA) {
          setDataObjectList(formDataObjectList);

          if (dataObjectValue) {
            const fields = _.find(formDataObjectList, { value: dataObjectValue })?.fields ?? [];
            setFieldList(fields);
          }
        } else if (dataLookupType === DATASOURCE_TYPE_EXTERNALDATASOURCE) {
          getExternalDatasources().then((res) => {
            const { ExternalDataSources, hasError } = res;
            if (!hasError) {
              if (ExternalDataSources) {
                setDataObjectList(
                  ExternalDataSources.map((source) => ({
                    label: source.Description,
                    value: source.Source,
                  })).sort((a, b) => a?.Description?.localeCompare(b?.Description))
                );
                if (dataObjectValue) {
                  const selectedDataSource = ExternalDataSources.find(
                    ({ Source }) => Source === dataObjectValue
                  );
                  const fieldsListFromDataSource = selectedDataSource?.SelectValueColumns.split(
                    ';'
                  ).map((field) => ({ value: field, label: field }));
                  setFieldList(fieldsListFromDataSource);
                }
              }
            }
          });
        } else if (dataLookupType === DATASOURCE_TYPE_SETTINGS) {
          setDataObjectList([{ value: 'Preferred Languages', label: 'Preferred Languages' }]);
          if (dataObjectValue) {
            setFieldList([
              { value: 'LANGUAGE_CODE', label: 'Language Code' },
              { value: 'LANGUAGE_NAME', label: 'Language Name' },
            ]);
          }
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataLookupType, dataObjectValue]);

  const dataSourceLinkBuilder = () => {
    let dataSourceLink = '';
    if (dataObjectValue && dataLookupType === DATASOURCE_TYPE_LISTDATA) {
      dataSourceLink = `${getEnvVar(
        'REACT_APP_ADMIN_APP_URL'
      )}/LST_LISTS_EDITOR.ASPX?ListObjectName=${dataObjectValue}&Token=${token}&NavAction=Edit`;
    }
    return dataSourceLink;
  };
  const dataSourceLink = dataObjectValue ? dataSourceLinkBuilder() : '';

  return (
    <div className={classes.root}>
      <div className={classes.row}>
        <TextControl
          label="Data Source Name"
          uita="DataSourceName"
          classes={classes}
          disabled={dataSourceEditAction === 'edit'}
          currentValue={dataSourceKey}
          errors={errors}
          errorLabel={ERROR_LABEL_DATASOURCE_KEY}
          onBlur={({ target }) => {
            if (dataSourceEditAction === 'new') {
              setErrors(
                validateDatasourceName({
                  dataSourceKey: target.value,
                  dataSources,
                  dataSourceEditAction,
                })
              );
            }

            setDataSourceKey(target.value);
            changeDataSource({
              dataSourceObject,
              property: 'dataSourceKey',
              propertyValue: target.value,
              setDatasourceObject,
            });
          }}
        />
      </div>
      <div className={classes.row}>
        <div className={classes.rowLabel}>
          <span>{'Data LookUp Type'}</span>
        </div>
        <div className={classes.rowColumn}>
          <Select
            ui-ta={getUITA('DataLookUpType')}
            fullWidth
            canDeselect={false}
            value={dataLookupType}
            disabled={isResolver(dataSourceObject?.dataSourceProperties)}
            onBlur={() => {
              changeDataSource({
                dataSourceObject,
                property: 'dataSourceType',
                propertyValue: dataLookupType,
                setDatasourceObject,
              });
              changeDataSource({
                dataSourceObject,
                property: 'dataSourceTypeStr',
                propertyValue: DATASOURCE_TYPE_ENUM[dataLookupType],
                setDatasourceObject,
              });
              changeDataSource({
                dataSourceObject,
                property: 'dataObjectValue',
                propertyValue: null,
                setDatasourceObject,
              });
              changeDataSource({
                dataSourceObject,
                property: 'dataObjectList',
                propertyValue: [],
                setDatasourceObject,
              });
              changeDataSource({
                dataSourceObject,
                property: 'columnList',
                propertyValue: [],
                setDatasourceObject,
              });
              setDatasourceObject(dataSourceObject);
            }}
            onChange={({ target: { value } }) => {
              if (dataLookupType !== value) {
                setDataLookupType(value);
                setDataObjectList([]);
                setDataObjectValue();
                setSelectedFields([]);
                setOrderByList([]);
                setFieldList([]);
                setDataKeyField('');
                setTempFilterConditions([]);
              }
            }}
          >
            {[
              { value: DATASOURCE_TYPE_SMARTSOLVEDATA, label: 'SmartSolve Data' },
              { value: DATASOURCE_TYPE_LISTDATA, label: 'List' },
              { value: DATASOURCE_TYPE_SETTINGS, label: 'Settings' },
              { value: DATASOURCE_TYPE_FORMDATA, label: 'Form Data' },
              { value: DATASOURCE_TYPE_EXTERNALDATASOURCE, label: 'External Data' },
            ].map(({ value, label }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </div>
      </div>
      <div className={classes.row}>
        <div className={classes.rowLabel}>
          <span>{'Data Object'}</span>
        </div>
        <div className={classes.rowColumn}>
          <Select
            ui-ta={getUITA('DataObject')}
            fullWidth
            canDeselect={false}
            value={dataObjectValue}
            disabled={isResolver(dataSourceObject?.dataSourceProperties)}
            onChange={({ target: { value } }) => {
              if (value === dataObjectValue) {
                return;
              }

              setDataObjectValue(value);
              setOrderByList([]);
              setTempFilterConditions([]);

              if (dataLookupType !== DATASOURCE_TYPE_LISTDATA) {
                setSelectedFields([]);
                changeDataSource({
                  dataSourceObject,
                  property: 'columnList',
                  propertyValue: [],
                  setDatasourceObject,
                });
                setDatasourceObject(dataSourceObject);
              }
            }}
            onBlur={() => {
              changeDataSource({
                dataSourceObject,
                property: 'dataObjectValue',
                propertyValue: dataObjectValue,
                setDatasourceObject,
              });
              changeDataSource({
                dataSourceObject,
                property: 'dataSourceName',
                propertyValue: dataObjectValue,
                setDatasourceObject,
              });
              changeDataSource({
                dataSourceObject,
                property: 'dataObjectList',
                propertyValue: dataObjectList,
                setDatasourceObject,
              });
              changeDataSource({
                dataSourceObject,
                property: 'orderBy',
                propertyValue: [],
                setDatasourceObject,
              });
            }}
          >
            {dataObjectList &&
              dataObjectList.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
          </Select>
          {dataSourceLink && (
            // eslint-disable-next-line react/jsx-no-target-blank
            <a style={{ color: 'blue', fontSize: '.9rem' }} target="_blank" href={dataSourceLink}>
              {` Edit List:  ${dataObjectValue}`}
            </a>
          )}
        </div>
      </div>
      {dataLookupType !== DATASOURCE_TYPE_LISTDATA &&
        dataLookupType !== DATASOURCE_TYPE_FORMDATA &&
        dataLookupType !== DATASOURCE_TYPE_SETTINGS && (
          <div className={classes.row}>
            <TextControl
              uita="DataKeyField"
              label="Data Key Field"
              classes={classes}
              disabled={dataLookupType !== DATASOURCE_TYPE_EXTERNALDATASOURCE}
              currentValue={dataKeyField}
              errors={errors}
              errorLabel={ERROR_LABEL_DATA_KEY_FIELD}
              onBlur={({ target }) =>
                changeDataSource({
                  dataSourceObject,
                  property: 'dataKeyField',
                  propertyValue: target.value,
                  setDatasourceObject,
                })
              }
            />
          </div>
        )}
      <div className={classes.row}>
        <div className={classes.rowLabel}>
          <span>{'Fields List'}</span>
        </div>
        <div className={classes.rowColumn}>
          <Select
            ui-ta={getUITA('FieldsList')}
            fullWidth
            multiple
            canDeselect={true}
            value={selectedFields}
            disabled={isResolver(dataSourceObject?.dataSourceProperties)}
            onChange={({ target: { value } }) => {
              setSelectedFields(value);
            }}
            onBlur={() => {
              changeDataSource({
                dataSourceObject,
                property: 'columnList',
                propertyValue: selectedFields,
                setDatasourceObject,
              });
              setDatasourceObject(dataSourceObject);
            }}
          >
            {fieldList.map(({ value, label }) => (
              <MenuItem key={value} value={value}>
                {`${label} (${value})`}
              </MenuItem>
            ))}
          </Select>
        </div>
      </div>
      {dataLookupType !== DATASOURCE_TYPE_LISTDATA &&
        dataLookupType !== DATASOURCE_TYPE_EXTERNALDATASOURCE &&
        dataLookupType !== DATASOURCE_TYPE_SETTINGS && (
          <div className={classes.row}>
            <div className={classes.rowLabel}>
              <span>{'Order By'}</span>
            </div>
            <div className={classes.rowColumn} ui-ta={getUITA('OrderBy')}>
              <OrderByTable
                fieldList={fieldList}
                currentRows={orderByList}
                changeDataSource={changeDataSource}
                dataSourceObject={dataSourceObject}
                setDatasourceObject={setDatasourceObject}
                setOrderByList={setOrderByList}
                disabled={isResolver(dataSourceObject?.dataSourceProperties)}
              />
            </div>
          </div>
        )}
      {dataLookupType !== DATASOURCE_TYPE_LISTDATA &&
        dataLookupType !== DATASOURCE_TYPE_FORMDATA &&
        dataLookupType !== DATASOURCE_TYPE_SETTINGS && (
          <div className={classes.row} style={{ marginTop: 25 }}>
            <div className={classes.rowLabel}>
              <span>{'Cache Data'}</span>
            </div>
            <div className={classes.rowColumn}>
              <RadioGroup
                ui-ta={getUITA('CacheData')}
                aria-label="label"
                name="Cache Data"
                className={classes.radio}
                value={isTrue(cacheData)}
                disabled={isResolver(dataSourceObject?.dataSourceProperties)}
                onChange={({ target: { value } }) => {
                  setCacheData(isTrue(value));
                  changeDataSource({
                    dataSourceObject,
                    property: 'cacheData',
                    propertyValue: isTrue(value),
                    setDatasourceObject,
                  });
                }}
              >
                <Radio value={true} label="Yes" />
                <Radio value={false} label="No" />
              </RadioGroup>
            </div>
          </div>
        )}
    </div>
  );
};

export default DatasourceModal;
